<template>
  <TransitionRoot as="template" :show="isDrawerOpen">
    <Dialog class="relative z-30" @close="closeDrawer">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-neutral-900/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full">
              <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                <div class="flex h-full flex-col bg-white dark:bg-neutral-800 shadow-xl">
                  <div
                    class="shrink-0 px-4 sm:px-6 pt-6 pb-4 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
                    <div class="flex items-start justify-between">
                      <DialogTitle class="text-base font-semibold leading-6 text-neutral-900 dark:text-white">
                        <i class="fa-sharp fa-solid fa-sparkles fa-fw mr-1" aria-hidden="true"></i>
                        {{ callToAction }}
                      </DialogTitle>

                      <div class="ml-3 flex gap-6 h-7 items-center">
                        <button
                          v-if="conversationId"
                          type="button"
                          class="w-6 h-6 flex items-center justify-center relative rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-white dark:ring-offset-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                          title="New Conversation"
                          @click="newConversation">
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">Start a new conversation</span>
                          <i class="fa-sharp fa-regular fa-pen-to-square fa-fw" aria-hidden="true"></i>
                        </button>

                        <RouterLink
                          to="/ask"
                          class="w-6 h-6 flex items-center justify-center relative rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-white dark:ring-offset-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                          title="Conversation History"
                          @click="closeDrawer">
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">View conversation history</span>
                          <i class="fa-sharp fa-regular fa-clock-rotate-left fa-fw" aria-hidden="true"></i>
                        </RouterLink>

                        <button
                          type="button"
                          class="w-6 h-6 flex items-center justify-center relative rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-white dark:ring-offset-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                          title="Close"
                          @click="closeDrawer">
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">Close the assistant</span>
                          <i class="fa-sharp fa-times fa-fw" aria-hidden="true"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="relative flex-1 min-h-0">
                    <NtAiConversation
                      :key="chatKey"
                      :conversation-id="conversationId"
                      @conversation-created="setConversation"
                      @conversation-unavailable="newConversation" />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { RouterLink } from 'vue-router'
import { mapActions, mapState } from 'pinia'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import NtAiConversation from './NtAiConversation.vue'

export default {
  components: {
    Dialog, // eslint-disable-line vue/no-reserved-component-names
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    RouterLink,
    NtAiConversation
  },
  data() {
    return {
      // Changed only when we want a different chat on screen, so that asking for a new
      // conversation remounts it empty. It deliberately does not follow the store's conversation
      // id: the chat creates its own conversation as it sends its first message, and keying on
      // that new id tore the chat down mid-send, closing the stream and losing the reply.
      chatKey: 'new'
    }
  },
  computed: {
    ...mapState(useAiAssistantStore, ['isDrawerOpen', 'callToAction', 'conversationId'])
  },
  created() {
    if (this.conversationId) {
      this.chatKey = this.conversationId
    }
  },
  methods: {
    ...mapActions(useAiAssistantStore, ['closeDrawer', 'setConversation', 'startNewConversation']),
    newConversation() {
      this.startNewConversation()
      this.chatKey = `new-${Date.now()}`
    }
  }
}
</script>
